/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast, no-nested-ternary */
import { FC, useState } from 'react';
import classNames from 'classnames';
import { translate } from 'translations';
import { ProgressIndicator, Tooltip, Divider, Icon } from 'learning-panel-components';
import { convertNumber, convertTimeFormat, formatFullTime } from '@components/learningPath/utils';
import { selectIsLeanUser } from '@redux/slices/userSlice';
import { useSelector } from 'react-redux';
import styles from './overview.module.scss';

const SkillTreeOverview: FC<SkillTreeOverviewProps> = (props: SkillTreeOverviewProps) => {
    const {
        score,
        progress,
        duration,
        languages,
        title,
        isSkillTree,
        onEditLanguages,
        isDiplomaEnabled,
        onShowCertificate,
        badgeName
    } = props;
    const [showEditLanguages, setShowEditLanguages] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const isLeanUser = useSelector(selectIsLeanUser);

    const onUnExpand = () => {
        setIsExpanded(false);
    };
    const onExpand = () => {
        setIsExpanded(true);
    };

    return isExpanded ? (
        <div className={styles['skill-tree-overview-wrapper']}>
            <div key="left-side-panel" className={styles['expand-button']} onClick={onUnExpand}>
                <Tooltip
                    key="left-side-panel-tooltip"
                    placement="right"
                    title={<div>{translate('CollapsePanel')}</div>}
                    enterDelay
                >
                    <div>
                        <Icon name="unExpand" key="unExpand" />
                    </div>
                </Tooltip>
            </div>
            <div className={styles['skill-tree-title']}>{title}</div>
            <div className={styles['skill-tree-diploma-wrapper']}>
                <div className={styles['skill-tree-diploma-text']}>{translate('Diploma')}</div>
                <img
                    src={
                        isSkillTree
                            ? `${process.env.PUBLIC_URL}/images/smallCertificateSkillTree.svg`
                            : badgeName
                            ? `${process.env.PUBLIC_URL}/images/learningPathBadges/${badgeName}.svg`
                            : `${process.env.PUBLIC_URL}/images/learningPathCertificateDetails.webp`
                    }
                    className={classNames(styles['skill-tree-diploma'], {
                        [styles['skill-tree-diploma-overly']]: !isDiplomaEnabled
                    })}
                    alt=""
                    onClick={() => (isDiplomaEnabled ? onShowCertificate() : undefined)}
                />
            </div>
            <Divider className={styles.divider} />
            <div className={styles['skill-tree-progress-wrapper']}>
                <div className={styles['progress-wrapper']}>
                    <div className={styles['progress-text']}>{translate('Progress')}</div>
                    <div className={styles['progress-value']}>{progress}%</div>
                </div>
                <ProgressIndicator direction={'horizontal'} value={progress} />
            </div>
            <Divider className={styles.divider} />
            <div className={styles['skill-tree-earned-wrapper']}>
                <div className={styles['earned-text']}>{translate('Earned')}</div>
                <Icon name="devCoin" className={styles.coin} />
                <div className={styles['earned-value']}>{score}</div>
            </div>
            <Divider className={styles.divider} />
            <div className={styles['skill-tree-duration-wrapper']}>
                <Icon name="clock" />
                <div className={styles['duration-value']}>~{formatFullTime(duration)}</div>
            </div>
            <Divider className={styles.divider} />

            <div
                className={styles['skill-tree-languages-wrapper']}
                onMouseOver={(): void => setShowEditLanguages(true)}
                onMouseLeave={(): void => setShowEditLanguages(false)}
            >
                <Icon name="language" />
                <div className={styles['languages-value']}>{languages.slice(0, 3).join(', ')}</div>
                {languages.length > 3 && (
                    <Tooltip
                        key="left-side-panel-tooltip"
                        placement="top"
                        title={<div>{languages.slice(3, languages.length).join(', ')}</div>}
                        enterDelay
                    >
                        <div className={styles['extend-languages']}>+{languages.length - 3}</div>
                    </Tooltip>
                )}
                {showEditLanguages && !isLeanUser && (
                    <Tooltip
                        key="left-side-panel-tooltip"
                        placement="right"
                        title={<div>{translate('Edit')}</div>}
                        enterDelay
                    >
                        <div>
                            <Icon name="editLanguages" className={styles['edit-icon']} onClick={onEditLanguages} />
                        </div>
                    </Tooltip>
                )}
            </div>
        </div>
    ) : (
        <div className={styles['skill-tree-overall-closed-wrapper']}>
            <div key="left-side-panel-un-expand" className={styles['un-expand-button']} onClick={onExpand}>
                <Icon name="expand" key="expand" />
            </div>
            <div data-testid="primary-course" className={styles['skill-tree-closed']}>
                <Icon name={isSkillTree ? 'pathThin' : 'path'} />
                <hr />
                <div className={styles.progress}>
                    <Icon name="primaryCourseClose" />
                    <ProgressIndicator direction={'vertical'} value={progress} />
                    <div className={styles.text}>{`${Math.trunc(progress)}%`}</div>
                </div>

                <hr />
                <div className={styles.score}>
                    <Icon name="devCoin" />
                    <div>{convertNumber(score)}</div>
                </div>
                <hr />
                <div className={styles.score}>
                    <Icon name="clock" />
                    <div>~{convertTimeFormat(duration)}</div>
                </div>
                <hr />
                <div className={styles.score}>
                    <Icon name="language" />
                    <div>{languages.length}</div>
                </div>
            </div>
        </div>
    );
};

export interface SkillTreeOverviewProps {
    title: string;
    score: number;
    progress: number;
    duration: number;
    languages: string[];
    isSkillTree: boolean;
    isDiplomaEnabled: boolean;
    onEditLanguages: Function;
    onShowCertificate: Function;
    badgeName?: string;
}

export default SkillTreeOverview;
